<script context="module" lang="ts">
  export type Header<R> = {
    active?: boolean;
    sort?: boolean | ((a: unknown, b: unknown) => number);
    dir?: "asc" | "desc";
    display: string;
    key: keyof R;
  };
</script>

<script lang="ts">
  type T = $$Generic;

  interface $$Slots {
    cell: {
      // slot name
      row: T;
      cell: {
        column: keyof T;
        value: T[keyof T];
      };
    };
    header: {};
    error: {};
    empty: {};
    row: { row: T };
  }

  export let skeleton = false;
  export let partialSkeleton = false;
  export let bodySkeleton;
  export let skeletonHeight;
  export let headers: Header<T>[] = [];
  export let rows: T[] = [];
  export let columns = headers ? `repeat(${headers.length},  1fr)` : null;
  export let empty = false;
  export let error = false;
  export let scrollThresholdOptions = {
    threshold: 20,
    horizontal: false,
  };

  import { createEventDispatcher } from "svelte";
  import SkeletonTable from "./SkeletonTable.svelte";
  import SkeletonRows from "./SkeletonRows.svelte";
  import { scrollThreshold } from "./js/helpers";

  let dispatch = createEventDispatcher();

  $: empty = rows?.length === 0 ?? false;
  $: render = !empty && !error && !skeleton;
</script>

{#if render}
  <div
    class="table-wrapper z-5 h-full w-full overflow-y-auto"
    on:thresholdOver
    use:scrollThreshold={scrollThresholdOptions}
  >
    <div class="table" style="--gtc:{columns}">
      <div class="contents">
        {#each headers as header}
          <div
            class="table-header sticky top-0 bg-white"
            class:active={header.active}
            class:sortable={header.sort}
            on:click={(e) => dispatch("click:header", header)}
          >
            <div class="table-header-text">
              <slot name="header" {header}>{header.display}</slot>
            </div>
          </div>
        {/each}
      </div>
      <div class="contents">
        {#if rows}
          {#if bodySkeleton}
            <SkeletonRows {...$$props} {skeletonHeight} />
          {:else}
            {#each rows as row}
              <slot name="row" {row}>
                <div class="contents" on:click={(e) => dispatch("click:row", row)}>
                  {#each headers as header}
                    {@const cell = { column: header.key, value: row[header.key] }}
                    <div
                      class="flex h-full items-center overflow-hidden truncate whitespace-nowrap !p-0 text-left align-middle"
                      on:click={(e) => dispatch("click:cell", cell)}
                    >
                      <slot name="cell" {row} {cell}>
                        {cell.value}
                      </slot>
                    </div>
                  {/each}
                </div>
              </slot>
            {/each}
          {/if}

          {#if partialSkeleton}
            <SkeletonRows {...$$props} {skeletonHeight} />
          {/if}
        {/if}
      </div>
    </div>
  </div>
{:else if skeleton}
  <SkeletonTable {...$$props} />
{:else if empty}
  <slot name="empty" />
{:else if error}
  <slot name="error" />
{/if}

<style>
  .table-wrapper {
    background-color: #ffffff;
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
    border: solid 0.5px #ccc;
  }

  ::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 7px;
    height: 7px;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: rgba(0, 0, 0, 0.5);
    box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
  }

  .table {
    display: grid;
    grid-template-columns: var(--gtc);
    border-collapse: separate;
    height: fit-content;
  }

  .table-header.sortable {
    cursor: pointer;
  }

  .table-header.inline-flex {
    display: inline-flex;
    gap: 20px;
  }

  .table-header {
    padding: 7px 0px 7px 16px;
    border-bottom: solid 2px #292929;
  }

  .table-header-text {
    display: flex;
    height: 100%;
    align-items: center;
    font-size: 14px;
    line-height: 1.29;
    color: #292929;
  }

  .table-header.active {
    border-bottom: 2px solid #fc3;
  }

  .table-header:last-child:after {
    content: "";
    width: 14px;
    height: 2px;
    position: absolute;
    bottom: 0;
    right: 0;
  }

  .table-cell {
    display: flex;
    height: 100%;
    align-items: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: left;
    padding: 0 !important;
    vertical-align: middle;
  }
</style>
